const apiVersion = 'v1';

import { ISite } from '@/Core.Service.Domain/Sites/Sites.Common/types/site.ts';

import {
  Device,
  DeviceSubType,
  IdAndName,
} from '@/Core.Service.Domain/Devices/Devices.Common/types/index.ts';

const getSitesForDevices = async (): Promise<ISite[]> => {
  const result = await window.axios.get(`/${apiVersion}/sites/portfolio`);
  const { data } = result.data;
  return (data as ISite[]).sort((a, b) => a.name.localeCompare(b.name));
};

const getDevicePropertiesBySiteIdAndSubTypeId = async (
  siteId: string | number,
  subTypeId: DeviceSubType
): Promise<Device[]> => {
  const result = await window.axios.get(
    `/${apiVersion}/device-properties/sites/${siteId}/sub-types/${subTypeId}`
  );
  const { data } = result.data;

  // Refer to DevicePropertiesContract.md for the expected contract response

  // Enrich devices with secondary property data sources
  const enrichedDevices = data.deviceProperties.map((device: Device) => ({
    ...device,
    deviceSubType: device.deviceSubType.name,
    secondaryProperties: device.secondaryProperties?.map((prop) => ({
      ...prop,
      dataSource: prop.dataSourceName
        ? data.secondaryPropertyDataSources[
            prop.dataSourceName as keyof typeof data.secondaryPropertyDataSources
          ]
        : undefined,
    })),
  })) as Device[];

  return enrichedDevices.sort((a: Device, b: Device) =>
    a.deviceName.localeCompare(b.deviceName)
  ) as Device[];
};

const getSubTypesBySiteId = async (siteId: string | number) => {
  const result = await window.axios.get(
    `/${apiVersion}/device-properties/sites/${siteId}/sub-types`
  );
  const { data } = result.data;
  return data;
};

const updateDeviceProperties = async (
  devices: Omit<Device, 'siteLocation'>[]
): Promise<void> => {
  const result = await window.axios.patch(
    `/${apiVersion}/device-properties`,
    devices
  );
  return result.data;
};

const getSiteLocationsForSiteId = async (siteId: string | number) => {
  const result = await window.axios.get(
    `/${apiVersion}/device-properties/sites/${siteId}/site-locations`
  );
  const { data } = result.data;
  return (data as IdAndName[])?.sort((a, b) => a.name.localeCompare(b.name));
};

export default {
  getSitesForDevices,
  getDevicePropertiesBySiteIdAndSubTypeId,
  getSubTypesBySiteId,
  updateDeviceProperties,
  getSiteLocationsForSiteId,
};
